import clsx from 'clsx'
import { Button } from '@/components/ui'
import { confirmDialog } from '@/components/Dialogs'
import { runActions, type RunAction, type RunActionKind, type RunActionTone, type RunStatus } from '@/lib/runStatus'

// Run-list action panel — renders the forward-transition buttons for a run's
// current status. The state machine + privilege guards live in runActions
// (src/lib/runStatus.ts) so they're unit-testable; this component just renders
// them, drives the reopen confirm dialogs, and reports the chosen action kind
// back to the parent (which owns the mutations). Extracted from Runs.tsx's
// former inline status===… button blocks, behaviour-preserving.
const TONE_CLASS: Record<RunActionTone, string> = {
  amber:   'text-amber-400 border-amber-800 hover:bg-amber-900/30',
  orange:  'text-orange-400 border-orange-800 hover:bg-orange-900/30',
  emerald: 'text-emerald-400 border-emerald-800 hover:bg-emerald-900/30',
}

export function RunActionButtons({ status, runNumber, isAdmin, isManager, canArchive, busy, onAction }: {
  status: RunStatus
  runNumber: string
  isAdmin: boolean
  /** Manager tier: may reopen a tipping-stopped run (not a COMPLETE one). */
  isManager?: boolean
  /** R1b: nothing tipped and no pallets, so Archive run is offered (see canArchiveRun). */
  canArchive?: boolean
  /** Disable all buttons while any run transition is in flight. */
  busy?: boolean
  onAction: (kind: RunActionKind) => void
}) {
  const actions = runActions(status, { isAdmin, isManager, canArchive })
  if (actions.length === 0) return null

  async function fire(a: RunAction) {
    if (a.confirm) {
      const ok = await confirmDialog({
        title:   a.confirm.title.replace('{run}', runNumber),
        message: a.confirm.message,
        confirm: a.confirm.confirm,
        tone:    a.confirm.tone,
      })
      if (!ok) return
    }
    onAction(a.kind)
  }

  return (
    <div className={clsx('grid gap-2', actions.length > 1 ? 'grid-cols-2' : 'grid-cols-1')}>
      {actions.map((a) => (
        <Button
          key={a.kind}
          variant="ghost"
          size="sm"
          className={clsx('w-full', TONE_CLASS[a.tone])}
          disabled={busy}
          onClick={(e) => { e.stopPropagation(); void fire(a) }}
        >
          {a.label}
        </Button>
      ))}
    </div>
  )
}
